import { safeStorage } from './storage.ts'
import { randomBytes } from './uuid.ts'

/**
 * **이 브라우저(기기)의 식별 표지** — 로그인 때 `LoginRequest.client` 로 실려 `user_session.client` 에 기록된다
 * (UAM-SES-02 · 계약 설명 「클라이언트 식별(예: electron/win)」).
 *
 * 🔴 **왜 있나**(eric 2026-09-22 「세션이 왜 이렇게 많아 — 로그인 시간이 아니라 기기별로 나와야 하는 거 아니야?」).
 *    서버는 로그인마다 세션 한 줄을 만들고, 기기를 가르는 축은 이 `client` 문자열 하나다. 종전에는 모든 웹 로그인이
 *    같은 값(`web/browser`)이라 한 브라우저에서 새 탭·새로고침·재로그인을 할 때마다 **구별 안 되는 줄이 한 줄씩 쌓였다**
 *    (실물: 한 계정에 26줄). 브라우저는 MAC 주소를 알 길이 없으므로, **한 번 만들어 저장한 무작위 표지**를 기기 이름으로
 *    쓴다 — 같은 브라우저 프로필에서 만든 세션은 같은 표지를 달고, 「내 계정」이 그것으로 묶는다.
 * 🔴 **개인정보가 아니다.** 표지는 이 사이트의 `localStorage` 에만 있는 무작위 값이고, 브라우저 종류·OS 는 UA 에서
 *    큰 분류만 읽는다(`chrome`·`firefox`… / `windows`·`mac`·`linux`…). 버전·정확한 기기 모델은 싣지 않는다.
 * ⚠ `VITE_CLIENT_ID` 가 있으면 그 값이 이긴다 — Tauri 설치형이 `tauri/win · 실험실 PC-1` 처럼 제 이름을 박는 길이다.
 * ⚠ 저장소를 지우면(사이트 데이터 삭제 · 시크릿 창) 새 표지가 나온다 — 그건 「새 기기」로 보이는 것이 맞다.
 */
export const DEVICE_STORAGE_KEY = 'ctcells:device'

/** 표지 길이 — 8자 hex. 사람이 읽고 견줄 수 있는 길이면 되고 유일성은 한 계정의 기기 수 안에서만 필요하다 */
const DEVICE_ID_HEX = 8

/*
 * 🔴 **난수원은 `uuid.ts` 한 곳에서 가져온다**(2026-09-23). 종전에는 여기서 `crypto` 를 직접
 *    불렀는데, **비보안 컨텍스트(IP 로 접속하는 dev 서버)에서 `crypto` 가 어디까지 있는지**를
 *    두 파일이 따로 알고 있었다 — 명령 ID 쪽이 그 가정 때문에 터졌다(`uuid.ts` 머리말).
 * ⚠ **다만 여기는 던지지 않는다.** 기기 표지는 사람이 읽는 라벨이고, 충돌해도 「내 계정」의
 *   세션 묶음이 섞이는 정도다. 멱등키(`uuid()`)와 규칙이 다르다 — 같이 묶지 마라.
 */
function randomHex(length: number): string {
  const bytes = randomBytes(Math.ceil(length / 2))
  if (bytes !== null) {
    return [...bytes]
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
      .slice(0, length)
  }
  return Math.random()
    .toString(16)
    .slice(2, 2 + length)
    .padEnd(length, '0')
}

/** 이 브라우저의 표지. 없으면 만들어 저장한다 — 저장에 실패하면(차단된 저장소) 매번 새 값이다 */
export function deviceId(): string {
  const stored = safeStorage.read(DEVICE_STORAGE_KEY)
  if (stored !== null && /^[0-9a-f]{4,32}$/.test(stored)) return stored
  const fresh = randomHex(DEVICE_ID_HEX)
  safeStorage.write(DEVICE_STORAGE_KEY, fresh)
  return fresh
}

/** UA 에서 브라우저·OS 의 큰 분류만 읽는다. 모르면 `browser`·`os` 다 — 지어내지 않는다 */
export function deviceKind(userAgent: string | undefined): { browser: string; os: string } {
  const ua = userAgent ?? ''
  const browser = /Edg\//.test(ua)
    ? 'edge'
    : /Firefox\//.test(ua)
      ? 'firefox'
      : /Chrome\//.test(ua)
        ? 'chrome'
        : /Safari\//.test(ua)
          ? 'safari'
          : 'browser'
  const os = /Windows/.test(ua)
    ? 'windows'
    : /Android/.test(ua)
      ? 'android'
      : /iPhone|iPad/.test(ua)
        ? 'ios'
        : /Mac OS/.test(ua)
          ? 'mac'
          : /Linux/.test(ua)
            ? 'linux'
            : 'os'
  return { browser, os }
}

/**
 * 로그인에 실을 클라이언트 식별 문자열. `web/<browser>-<os>/<표지>` — 예 `web/chrome-linux/3f9a2c1b`.
 * `VITE_CLIENT_ID` 가 있으면 그것을 그대로 쓴다(설치형이 제 이름을 박는 길).
 */
export function clientIdentity(): string {
  const override = import.meta.env.VITE_CLIENT_ID?.trim()
  if (override) return override
  const { browser, os } = deviceKind(
    typeof navigator === 'undefined' ? undefined : navigator.userAgent,
  )
  return `web/${browser}-${os}/${deviceId()}`
}
